import React, { useEffect, useRef, useState } from 'react'

import './schedules.css';
import { exportToExcel } from "../../../utils/exportExcel";
import ConfirmModal from '../../../components/confirmModal';
import { mixedNaturalSort } from '../../../utils/generalFunctions';
import BootstrapTooltip from '../../../components/tooltip';
import NewSchedule from '../../../components/newSchedule';
import EditSchedules from '../../../components/editSchedules';
import ViewSchedules from '../../../components/viewSchedules';

const LAYOUTS = {
  SCHEDULES: 0,
  NEW: 1,
  EDIT: 2,
  VIEW: 3,
};

const Schedules = ({ devices, userRoute, geoTags, geofences, userSchedules, userUpdate, updateSchedule, scheduleRegister, deleteSchedule, showAlert }) => {
  const [filterText, setFilterText] = useState(''); // Texto del filtro
  const [filterOption, setFilterOption] = useState('all'); // Filtro avanzado (pares, impares, activos, inactivos)
  const [order, setOrder] = useState('init'); // Orden actual: 'init', 'asc', 'desc'
  const [orderBy, setOrderBy] = useState(null); // Columna para ordenar
  const [isModified, setIsModified] = useState(false); // Nuevo estado para rastrear modificaciones
  const [scheduleList, setScheduleList] = useState(() => structuredClone(userSchedules)); // Lista de horarios

  const [selectedItems, setSelectedItems] = useState([]); // Items seleccionados
  const headerCheckboxRef = useRef(null); // Referencia para el checkbox del encabezado

  const [filteredGeofences, setFilteredGeofences] = useState([]); // Columna para ordenar

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingVehicleChange, setPendingVehicleChange] = useState(null);

  const [showedLayout, setShowedLayout] = useState(LAYOUTS.SCHEDULES); // 0 para tabla, 1 para nuevo horario, 2 para editar horario

  useEffect(() => {
    // Obtener el id de "Checadas"
    const checadasTag = geoTags.find((item) => item.name === 'Checadas');
    if (checadasTag) {
      const checadasId = checadasTag.id;

      // Filtrar geofences que contengan el id de "Checadas" en sus tags
      const filtered = geofences.filter(
        (geofence) => Array.isArray(geofence.tags) && geofence.tags.includes(checadasId)
      );

      setFilteredGeofences(filtered);
    }
  }, [geoTags, geofences]);

  const handleSort = (column) => {
    const newOrder = orderBy === column
      ? (order === 'init' ? 'asc' : (order === 'asc' ? 'desc' : 'init'))
      : 'asc';

    setOrder(newOrder);
    setOrderBy(column);

    if (newOrder === 'init') {
      // Restaurar la lista original si el orden es 'init'
      setScheduleList(userSchedules);
    } else {
      const isAsc = newOrder === 'asc';

      // Ordenar por `label` del dispositivo para la columna 'vehicleId'
      const sortedSchedules = [...scheduleList].sort((a, b) => {
        if (column === 'name') {
          return isAsc
            ? a.name.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: 'base'
            })
            : b.name.localeCompare(a.name, undefined, {
              numeric: true,
              sensitivity: 'base'
            });
        } else if (column === 'vehicleId') {
          const deviceA = devices.find(device => device.id === a.vehicleId)?.label || '';
          const deviceB = devices.find(device => device.id === b.vehicleId)?.label || '';
          return isAsc ? deviceA.localeCompare(deviceB) : deviceB.localeCompare(deviceA);
        } else {
          return isAsc
            ? (a[column] < b[column] ? -1 : 1)
            : (a[column] > b[column] ? 1 : -1);
        }
      });

      setScheduleList(sortedSchedules);
    }
  };


  const getSortIcon = (column) => {
    if (orderBy !== column || order === 'init') return <span>-</span>;
    return order === 'asc' ? (
      <span className='material-symbols-rounded'>arrow_upward_alt</span>
    ) : (
      <span className='material-symbols-rounded'>arrow_downward_alt</span>
    );
  };

  const applyFilters = () => {
    let filteredDevices = userSchedules;

    // Filtrar por texto
    if (filterText) {
      filteredDevices = filteredDevices.filter((device) =>
        device.name.toLowerCase().includes(filterText)
      );
    }

    // Filtrar por opción avanzada
    switch (filterOption) {
      case 'even': // IDs pares
        filteredDevices = filteredDevices.filter((_, index) => (index + 1) % 2 === 0);
        break;
      case 'odd': // IDs impares
        filteredDevices = filteredDevices.filter((_, index) => (index + 1) % 2 !== 0);
        break;
      default:
        break;
    }

    setScheduleList(filteredDevices);
  };

  useEffect(() => {
    applyFilters();
  }, [filterText, filterOption, userSchedules]);

  const saveChanges = () => {
    // Filtrar los horarios que han sido modificados
    const modifiedSchedules = scheduleList.filter((schedule) => {
      const originalSchedule = userSchedules.find((s) => s._id === schedule._id);

      // Comparar cambios en vehicleId
      return originalSchedule && originalSchedule.vehicleId !== schedule.vehicleId;
    });

    // Verificar si hay cambios para guardar
    if (modifiedSchedules.length > 0) {
      // console.log("Horarios modificados:", modifiedSchedules);

      // Llama a la función para actualizar los horarios en el backend
      updateSchedule(scheduleList, modifiedSchedules);
    } else {
      console.log("No hay cambios en los horarios.");
    }

    // Restablecer estado de modificación
    setIsModified(false);
  };

  // Actualizar el estado "indeterminado" del checkbox del encabezado
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        selectedItems.length > 0 && selectedItems.length < scheduleList.length;
    }
  }, [selectedItems, scheduleList.length]);

  const handleRowClick = (index) => {
    setSelectedItems((prevSelected) =>
      prevSelected.includes(index)
        ? prevSelected.filter((i) => i !== index)
        : [...prevSelected, index]
    );
  };

  const handleHeaderClick = () => {
    if (selectedItems.length === scheduleList.length) {
      setSelectedItems([]); // Desmarcar todos
    } else {
      setSelectedItems(scheduleList.map((_, i) => i)); // Seleccionar todos
    }
  };

  const handleVehicleChange = (scheduleId, newVehicleId) => {
    const isAssigned = scheduleList.some(
      (schedule) => schedule.vehicleId === parseInt(newVehicleId) && schedule._id !== scheduleId
    );

    if (isAssigned) {
      // Guardar cambios pendientes y mostrar el modal
      setPendingVehicleChange({ scheduleId, newVehicleId });
      setIsConfirmModalOpen(true);
    } else {
      applyVehicleChange(scheduleId, newVehicleId);
    }
  };

  const applyVehicleChange = (scheduleId, newVehicleId) => {
    setScheduleList((prevList) =>
      prevList.map((schedule) => {
        // Desasignar del horario anterior
        if (schedule.vehicleId === parseInt(newVehicleId) && schedule._id !== scheduleId) {
          return { ...schedule, vehicleId: null };
        }
        // Asignar al nuevo horario
        if (schedule._id === scheduleId) {
          return { ...schedule, vehicleId: parseInt(newVehicleId) || null };
        }
        // Mantener los horarios restantes sin cambios
        return schedule;
      })
    );
    setIsModified(true);
  };

  const handleViewClick = () => {
    const schedulesToView = scheduleList.filter((_, index) => selectedItems.includes(index));
    setSelectedSchedules(schedulesToView);
    setShowedLayout(LAYOUTS.VIEW);
  };

  const handleEditClick = () => {
    const schedulesToEdit = scheduleList.filter((_, index) => selectedItems.includes(index));
    setSelectedSchedules(schedulesToEdit);
    setShowedLayout(LAYOUTS.EDIT);
  };

  const handleSave = (updatedSchedule) => {
    // Validar si hay cambios comparando los campos relevantes
    const originalSchedule = userSchedules.find(schedule => schedule._id === updatedSchedule._id);

    const hasChanges = originalSchedule &&
      (
        originalSchedule.name !== updatedSchedule.name ||
        originalSchedule.vehicleId !== updatedSchedule.vehicleId ||
        JSON.stringify(originalSchedule.turns) !== JSON.stringify(updatedSchedule.turns)
      );

    if (hasChanges) {
      const newSchedulesList = userSchedules.map(schedule => {
        return schedule._id === updatedSchedule._id ? updatedSchedule : schedule.vehicleId === updatedSchedule.vehicleId ? { ...schedule, vehicleId: null } : schedule;
      });

      const conflictingSchedule = userSchedules.find(
        schedule => updatedSchedule._id !== schedule._id && updatedSchedule.vehicleId !== null && schedule.vehicleId === updatedSchedule.vehicleId
      );

      const modifiedSchedules = conflictingSchedule ? [{ ...conflictingSchedule, vehicleId: null }, updatedSchedule] : [updatedSchedule];

      // Llama a la función para actualizar los horarios en el backend
      updateSchedule(newSchedulesList, modifiedSchedules);
    } else {
      console.log("No hay cambios en los horarios.");
    }
  };

  const handleDeleteClick = () => {
    setItemsToDelete(scheduleList.filter((_, index) => itemsToDelete.includes(index))); // Guardar los seleccionados.
    setIsDeleteModalOpen(true); // Mostrar el modal.
  };

  const renderLayout = () => {

    switch (showedLayout) {

      case LAYOUTS.NEW:
        return (
          <NewSchedule
            devices={devices}
            userSchedules={userSchedules}
            userRoute={userRoute}
            geofences={filteredGeofences}
            userUpdate={userUpdate}
            scheduleRegister={scheduleRegister}
            showAlert={showAlert}
            setShowedLayout={setShowedLayout}
          />
        );

      case LAYOUTS.EDIT:
        return (
          <EditSchedules
            showedLayout={showedLayout}
            onClose={() => setShowedLayout(LAYOUTS.LIST)}
            schedules={selectedSchedules}
            devices={devices}
            geofences={geofences}
            userSchedules={userSchedules}
            userRoute={userRoute}
            userUpdate={userUpdate}
            onSave={handleSave}
            scheduleRegister={scheduleRegister}
            updateSchedule={updateSchedule}
            showAlert={showAlert}
            setShowedLayout={setShowedLayout}
          />
        );

      case LAYOUTS.VIEW:
        return (
          <ViewSchedules
            onClose={() => setShowedLayout(LAYOUTS.LIST)}
            schedules={selectedSchedules}
            setShowedLayout={setShowedLayout}
          />
        );

      default:
        return renderTable();
    }
  };

  const renderTable = () => (
    <div className='card_content' style={{ display: 'flex', flexDirection: 'column' }}>
      <div className='actions_container'>
        <div className='filters_container'>
          <span>Filtrar por nombre:</span>
          <input
            type="text"
            placeholder="Horario..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value.toLowerCase())}
            className="filter_input"
          />
          <span>Mostrar:</span>
          <select id='schedules_show_select' value={filterOption} onChange={(e) => setFilterOption(e.target.value)} className="filter_select">
            <option value="all">Todos</option>
            <option value="even">Solo pares</option>
            <option value="odd">Solo impares</option>
          </select>
        </div>
        <div className='buttons_container'>
          <BootstrapTooltip title="Descargar">
            <button className='download_btn' disabled={selectedItems.length === 0} onClick={() => exportToExcel(scheduleList.filter((_, index) => selectedItems.includes(index)))}>
              <span className="material-symbols-rounded">download</span>
            </button>
          </BootstrapTooltip>
          <BootstrapTooltip title="Ver">
            <button className='view_btn' disabled={selectedItems.length === 0} onClick={handleViewClick}>
              <span className="material-symbols-rounded">visibility</span>
            </button>
          </BootstrapTooltip>
          <BootstrapTooltip title="Editar">
            <button className='edit_btn' disabled={selectedItems.length === 0} onClick={handleEditClick}>
              <span className="material-symbols-rounded">edit</span>
            </button>
          </BootstrapTooltip>
          <BootstrapTooltip title="Eliminar">
            <button className='delete_btn' disabled={selectedItems.length === 0} onClick={handleDeleteClick}>
              <span className="material-symbols-rounded">delete_forever</span>
            </button>
          </BootstrapTooltip>
          <ConfirmModal title={'Confirmar Eliminación'} text={'¿Estás seguro de que deseas eliminar los horarios seleccionados?'} isOpen={isDeleteModalOpen} onClose={setIsDeleteModalOpen}
            confirmFunction={() => {
              deleteSchedule(selectedItems.map(index => scheduleList[index].name));
              setSelectedItems([]);
              setIsDeleteModalOpen(false);
            }} />
          <button className='save_changes_btn' disabled={!isModified} onClick={saveChanges}>Guardar Cambios</button>
          {/* <button className='new_button' onClick={handleOpenModal}>Nuevo +</button> */}
          <button className="new_button" onClick={() => setShowedLayout(LAYOUTS.NEW)}>
            Nuevo +
          </button>
          {/* <SchedulesModal devices={devices} userSchedules={userSchedules} userRoute={userRoute} geofences={filteredGeofences} userUpdate={userUpdate} scheduleRegister={scheduleRegister} showAlert={showAlert} /> */}
        </div>
      </div>
      <ConfirmModal title={'Confirmación'} text={'Este vehículo ya se encuentra asignado a otro horario. ¿Desea asignarlo a este horario de cualquier manera?'} isOpen={isConfirmModalOpen} onClose={setIsConfirmModalOpen}
        confirmFunction={() => {
          if (pendingVehicleChange) {
            applyVehicleChange(
              pendingVehicleChange.scheduleId,
              pendingVehicleChange.newVehicleId
            );
          }
          setIsConfirmModalOpen(false);
        }} />
      {/* <ConfirmVehicleChangeModal /> */}
      <div className='table_wrapper'>
        <table className='devices_table'>
          <thead>
            <tr>
              <th className='checkbox_cell' onClick={handleHeaderClick}>
                <input
                  type="checkbox"
                  ref={headerCheckboxRef}
                  onChange={(e) =>
                    setSelectedItems(
                      e.target.checked ? scheduleList.map((_, i) => i) : []
                    )
                  }
                  checked={
                    selectedItems.length === scheduleList.length &&
                    scheduleList.length > 0
                  }
                  onClick={(e) => e.stopPropagation()} // Evitar doble activación
                />
              </th>
              <th onClick={() => handleSort('name')}><span>Horario</span> {getSortIcon('name')}</th>
              <th onClick={() => handleSort('vehicleId')}><span>Vehículo asignado</span> {getSortIcon('vehicleId')}</th>
            </tr>
          </thead>
          <tbody>
            {scheduleList.map((schedule, index) => (
              <tr key={schedule._id}>
                <td className='checkbox_cell'
                  onClick={() => handleRowClick(index)}
                  style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(index)}
                    onChange={() => handleRowClick(index)}
                    onClick={(e) => e.stopPropagation()} // Evitar doble activación
                  />
                </td>
                <td style={{ cursor: 'pointer' }} onClick={() => handleRowClick(index)}>{schedule.name}</td>
                <td>
                  <select
                    id={'schedule ' + index}
                    value={schedule.vehicleId || ''}
                    onChange={(e) =>
                      handleVehicleChange(schedule._id, e.target.value)
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="">No asignado</option>
                    {devices.slice().sort(mixedNaturalSort).map((device) => (
                      <option key={device.id} value={device.id}>
                        {device.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );


  return (
    <div className='schedules_container'>
      {renderLayout()}
    </div>
  )
}

export default Schedules